/**
 * Universal Error Alert Component
 * Provides consistent, actionable error messages across the application
 */

import { AlertCircle, X, Info, AlertTriangle, Server } from 'lucide-react';

export type ErrorSeverity = 'error' | 'warning' | 'info';

export interface ErrorAlertProps {
  /** The main error message */
  title?: string;
  /** Detailed error description */
  message?: string;
  /** Field-specific errors for forms */
  fieldErrors?: Record<string, string>;
  /** Whether this is a system error (for dev reporting) */
  isSystemError?: boolean;
  /** HTTP status code if available */
  statusCode?: number;
  /** Error severity level */
  severity?: ErrorSeverity;
  /** Callback to dismiss the error */
  onDismiss?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Universal error display component that provides:
 * - Clear, actionable error messages for users
 * - System error indicators for dev troubleshooting
 * - Field-level validation errors for forms
 * - Consistent styling across the app
 */
export function ErrorAlert({
  title,
  message,
  fieldErrors,
  isSystemError,
  statusCode,
  severity = 'error',
  onDismiss,
  className = '',
}: ErrorAlertProps) {
  const hasFieldErrors = fieldErrors && Object.keys(fieldErrors).length > 0;
  const hasMessage = message && message.trim().length > 0;
  
  // Don't render if no error content
  if (!hasMessage && !hasFieldErrors && !title) return null;

  // Severity-based styling
  const severityStyles = {
    error: {
      container: 'bg-danger-500/10 border-danger-500/30',
      icon: 'text-danger-400',
      title: 'text-danger-400',
      message: 'text-gray-600 dark:text-warm-sand',
      fieldError: 'text-danger-400',
      systemBadge: 'bg-danger-500/20 text-danger-400',
    },
    warning: {
      container: 'bg-amber-450/10 border-amber-450/30',
      icon: 'text-amber-450',
      title: 'text-amber-450',
      message: 'text-gray-600 dark:text-warm-sand',
      fieldError: 'text-amber-450',
      systemBadge: 'bg-amber-450/20 text-amber-450',
    },
    info: {
      container: 'bg-blue-500/10 border-blue-500/30',
      icon: 'text-blue-400',
      title: 'text-blue-400',
      message: 'text-gray-600 dark:text-warm-sand',
      fieldError: 'text-blue-400',
      systemBadge: 'bg-blue-500/20 text-blue-400',
    },
  };

  const styles = severityStyles[severity];

  // Icon based on severity
  const Icon = severity === 'error' ? AlertCircle : severity === 'warning' ? AlertTriangle : Info;

  return (
    <div className={`p-4 rounded-xl border animate-shake ${styles.container} ${className}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${styles.icon}`} />
        <div className="flex-1 min-w-0">
          {/* Title row with optional dismiss button */}
          {(title || isSystemError) && (
            <div className="flex items-center gap-2 flex-wrap">
              {title && (
                <p className={`font-medium ${styles.title}`}>
                  {title}
                </p>
              )}
              {isSystemError && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${styles.systemBadge}`}>
                  <Server className="w-3 h-3 inline mr-1" />
                  System Error{statusCode ? ` (${statusCode})` : ''}
                </span>
              )}
            </div>
          )}
          
          {/* Main error message */}
          {hasMessage && (
            <p className={`text-sm mt-1 ${styles.message}`}>
              {message}
            </p>
          )}
          
          {/* Field-specific validation errors */}
          {hasFieldErrors && (
            <ul className={`mt-2 text-sm space-y-0.5 ${styles.fieldError}`}>
              {Object.entries(fieldErrors).map(([field, errorMsg]) => (
                <li key={field} className="flex items-start gap-1.5">
                  <span className="mt-1 w-1 h-1 rounded-full bg-current flex-shrink-0" />
                  <span>
                    <span className="font-medium capitalize">{field.replace(/([A-Z])/g, ' $1').trim()}:</span>{' '}
                    {errorMsg}
                  </span>
                </li>
              ))}
            </ul>
          )}
          
          {/* Dev guidance for system errors */}
          {isSystemError && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-300 dark:border-charcoal-600 pt-2">
              If this persists, please report this error to support with the timestamp and any details above.
            </p>
          )}
        </div>
        
        {/* Dismiss button */}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="p-1 hover:bg-white/20 dark:hover:bg-black/20 rounded-lg transition-colors flex-shrink-0"
            aria-label="Dismiss error"
          >
            <X className={`w-4 h-4 ${styles.icon}`} />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Hook to extract error details from API responses for use with ErrorAlert
 */
export function useErrorDetails(error: any) {
  if (!error) return null;

  const statusCode = error.response?.status;
  const data = error.response?.data;
  
  // Determine if this is a system error (5xx) or user error (4xx)
  const isSystemError = statusCode >= 500 || !statusCode;
  
  // Extract field errors from validation failures
  const fieldErrors = data?.errors || {};
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  
  // Determine severity
  let severity: ErrorSeverity = 'error';
  if (statusCode >= 500) severity = 'error';
  else if (statusCode === 409 || statusCode === 422) severity = 'warning';
  else if (statusCode === 404) severity = 'info';
  
  return {
    title: hasFieldErrors ? 'Please fix the following errors' : 'Error',
    message: data?.message || error.message || 'An unexpected error occurred',
    fieldErrors,
    isSystemError,
    statusCode,
    severity,
    hasFieldErrors,
  };
}

export default ErrorAlert;
